import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { loginSchema, type LoginDto } from '@fp3/shared'
import axios from 'axios'
import { useLogin } from '../hooks/useLogin'
import { useResendVerification } from '../hooks/useResendVerification'
import { getErrorMessage } from '@/lib/utils/errors'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

export const LoginForm = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm<LoginDto>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange',
  })

  const { mutate: login, isPending, error } = useLogin()
  const {
    mutate: resendVerification,
    isPending: isResending,
    isSuccess: isResent,
  } = useResendVerification()
  const [resendEmail, setResendEmail] = useState<string | null>(null)

  const isEmailNotVerified =
    axios.isAxiosError(error) &&
    error.response?.status === 403 &&
    error.response?.data?.code === 'EMAIL_NOT_VERIFIED'

  const onSubmit = (data: LoginDto) => {
    setResendEmail(data.email)
    login(data)
  }

  const hasValidationErrors = Object.keys(errors).length > 0

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Login</CardTitle>
        <CardDescription>Enter your credentials to access your account</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {isEmailNotVerified && (
            <div
              className="p-3 text-sm text-foreground bg-warning/10 border border-warning/30 rounded-md"
              role="alert"
            >
              <p className="font-medium mb-1">Email not verified</p>
              <p>Please verify your email before logging in.</p>
              {isResent ? (
                <p className="text-xs mt-2">Verification email resent.</p>
              ) : (
                <button
                  type="button"
                  className="text-xs mt-2 text-primary hover:underline"
                  disabled={isResending}
                  onClick={() => resendEmail && resendVerification(resendEmail)}
                >
                  {isResending ? 'Sending...' : 'Resend verification email'}
                </button>
              )}
            </div>
          )}
          {error && !hasValidationErrors && !isEmailNotVerified && (
            <div
              className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md"
              role="alert"
              data-testid="auth-login-error-alert"
            >
              <p className="font-medium mb-1">Login failed</p>
              <p>{getErrorMessage(error)}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'email-error' : undefined}
              data-testid="auth-login-email-input"
              {...register('email')}
            />
            {errors.email && (
              <p
                id="email-error"
                className="text-sm text-destructive"
                data-testid="auth-login-email-error"
              >
                {errors.email.message}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              aria-invalid={!!errors.password}
              aria-describedby={errors.password ? 'password-error' : undefined}
              data-testid="auth-login-password-input"
              {...register('password')}
            />
            {errors.password && (
              <p
                id="password-error"
                className="text-sm text-destructive"
                data-testid="auth-login-password-error"
              >
                {errors.password.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isPending || !isDirty || !isValid}
            data-testid="auth-login-submit"
          >
            {isPending ? 'Logging in...' : 'Login'}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
